import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BotStatusDto } from '@rhcst/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { shortAddress } from '../lib/format.js';
import { TallyRow } from '../components/TallyLight.js';
import { toLamps, derivePosture } from '../components/InterlockStrip.js';
import type { Interlocks } from '../components/InterlockStrip.js';

/**
 * Bot status and the kill switch.
 *
 * The kill switch is the only control in this interface that changes whether the
 * system can spend money, and it is deliberately asymmetric:
 *
 * - **Engaging is one click.** Stopping trading must never be gated behind a
 *   confirmation the operator has to fight through in a hurry.
 * - **Releasing requires an explicit second step**, and is disabled outright when
 *   the server environment holds the switch — with the reason shown, so the UI
 *   explains itself rather than presenting a dead button.
 *
 * The redesign makes it a physical rocker rather than a pair of buttons. The
 * knob's *position* is the reading: an operator glancing at the screen sees which
 * end it sits at before reading any label, which is the same reason the strip
 * carries colour temperature. A checkbox would have neither property.
 *
 * v2 (docs/design_handoff_console_redesign/) trims this page rather than
 * growing it: the per-factory scanner detail panel that used to live here
 * moved to the new `/system` page (superseded by that page's fuller
 * `GET /api/scanner/status` table), leaving a small link-out card in its
 * place. The kill switch is safety-critical and should not be visually
 * diluted by a growing pile of operational detail on the same screen — an
 * open design question in the backend audit that this settles as "split,
 * not merge". Kill switch, wallet, the 4-item interlock grid, trading
 * flags, totals, and integrations all stay exactly as they were.
 */

export interface BotStatusPageProps {
  status: BotStatusDto | null;
  stale: boolean;
  onRefresh: () => Promise<void>;
}

interface Flag {
  label: string;
  value: boolean;
  /** Whether `true` is the permissive state. Drives colour, not layout. */
  trueIsPermissive: boolean;
}

function FlagChip({ flag }: { flag: Flag }) {
  const permissive = flag.trueIsPermissive ? flag.value : !flag.value;
  return (
    <span className="chip" data-tone={permissive ? 'neutral' : 'muted'}>
      {flag.label} {flag.value ? 'on' : 'off'}
    </span>
  );
}

export function BotStatus({ status, stale, onRefresh }: BotStatusPageProps) {
  const [busy, setBusy] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(engaged: boolean) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.post<{ applied: boolean; message: string }>('/bot/kill-switch', {
        engaged,
      });
      setMessage(result.message);
      setConfirmRelease(false);
      await onRefresh();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not change the kill switch.');
    } finally {
      setBusy(false);
    }
  }

  if (!status) {
    return (
      <div className="stack">
        <div className="screen-head">
          <h1 className="screen-title">Bot</h1>
        </div>
        <p className="notice">Loading status…</p>
      </div>
    );
  }

  const ks = status.kill_switch;
  const interlocks: Interlocks = {
    killSwitch: status.interlocks.kill_switch_engaged,
    tradingEnabled: status.interlocks.trading_enabled,
    botWalletEnabled: status.interlocks.bot_wallet_enabled,
  };
  const posture = derivePosture(interlocks);
  const lamps = toLamps(interlocks, status.wallet.key_configured);

  // The rocker reads "armed" when trading is possible, which is the inverse of
  // the switch being engaged. The label the operator needs is "can it spend",
  // not "is a flag set".
  const canSpend = posture === 'armed';

  const flags: Flag[] = [
    { label: 'Trading', value: status.trading.trading_enabled, trueIsPermissive: true },
    { label: 'Auto trading', value: status.trading.auto_trading_enabled, trueIsPermissive: true },
    {
      label: 'Telegram approval',
      value: status.trading.telegram_approval_required,
      trueIsPermissive: false,
    },
    { label: 'Hot wallet', value: status.wallet.enabled, trueIsPermissive: true },
    { label: 'Scanner', value: status.scanner.enabled, trueIsPermissive: true },
  ];

  return (
    <div className="stack">
      <div className="screen-head">
        <h1 className="screen-title">Bot</h1>
        <div className="screen-actions">
          {stale && <span className="stale-banner">Status is stale — the API may be unreachable.</span>}
          <button className="btn btn-mini" type="button" onClick={() => void onRefresh()}>
            Refresh
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ alignItems: 'start' }}>
        {/* --- Left: the control itself ------------------------------------ */}
        <div className="stack">
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Kill switch</h2>
              <span className="label label-dim">{ks.source}</span>
            </div>
            <div className="panel-body stack">
              {/* The rocker. Position is the reading. */}
              <button
                className="rocker"
                type="button"
                data-armed={canSpend}
                aria-pressed={ks.engaged}
                aria-label={
                  ks.engaged
                    ? 'Kill switch engaged. Release it to permit trading.'
                    : 'Kill switch released. Engage it to stop all trading.'
                }
                disabled={busy || (!ks.engaged ? false : !ks.releasable_from_dashboard)}
                onClick={() => {
                  if (ks.engaged) {
                    // Releasing is the dangerous direction, so it takes two
                    // deliberate actions.
                    setConfirmRelease(true);
                  } else {
                    void submit(true);
                  }
                }}
              >
                <span className="rocker-track">
                  <span className="rocker-knob" />
                </span>
                <span className="rocker-state">
                  {canSpend ? 'Armed' : 'Safed'}
                  <br />
                  <span className="label label-dim">
                    {ks.engaged ? 'switch engaged' : 'switch released'}
                  </span>
                </span>
              </button>

              <p className="panel-note">{ks.reason}</p>

              {/* Engaging is always available and never confirmed. */}
              {!ks.engaged && (
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={() => void submit(true)}
                  disabled={busy}
                >
                  {busy ? 'Stopping…' : 'Stop trading now'}
                </button>
              )}

              {ks.engaged && !ks.releasable_from_dashboard && (
                <p className="notice" data-tone="alarm">
                  Held by the server environment. This cannot be released from the
                  dashboard — set <code className="data-xs">TRADING_KILL_SWITCH=false</code> and
                  restart.
                </p>
              )}

              {ks.engaged && ks.releasable_from_dashboard && confirmRelease && (
                <div className="hazard-notice">
                  <div className="hazard-notice-title">Confirm release</div>
                  Releasing the kill switch permits the bot to spend from the hot
                  wallet, subject to every other interlock below.
                  <div className="form-actions">
                    <button
                      className="btn"
                      type="button"
                      onClick={() => void submit(false)}
                      disabled={busy}
                    >
                      {busy ? 'Releasing…' : 'Release'}
                    </button>
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => setConfirmRelease(false)}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {message && <p className="notice">{message}</p>}
              {error && (
                <p className="notice" data-tone="alarm" role="alert">
                  {error}
                </p>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Hot wallet</h2>
            </div>
            <div className="panel-body">
              <dl className="facts">
                <div className="fact">
                  <dt className="fact-key">Address</dt>
                  <dd className="fact-value">
                    {status.wallet.address ? shortAddress(status.wallet.address, 10, 8) : 'none configured'}
                  </dd>
                </div>
                <div className="fact">
                  <dt className="fact-key">Enabled</dt>
                  <dd className="fact-value">{status.wallet.enabled ? 'yes' : 'no'}</dd>
                </div>
                <div className="fact">
                  <dt className="fact-key">Key present</dt>
                  {/* Presence only. The key itself never leaves the worker
                      process, and no API response carries it (ADR-043). */}
                  <dd className="fact-value">{status.wallet.key_configured ? 'yes' : 'no'}</dd>
                </div>
              </dl>
            </div>
          </section>

          {/* v2: replaces the old per-factory scanner detail panel, which
              moved to /system. */}
          <Link to="/system" className="card" style={{ borderColor: 'var(--chrome-accent-border)' }}>
            <span className="label" style={{ color: 'var(--chrome-accent)' }}>
              Worker, scanner &amp; risk health →
            </span>
            <p className="panel-note" style={{ marginTop: 'var(--s-1)' }}>
              Moved to a dedicated System page so the kill switch stays
              uncluttered.
            </p>
          </Link>
        </div>

        {/* --- Right: interlocks, flags, totals ---------------------------- */}
        <div className="stack">
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Interlocks</h2>
              <span className="label label-dim">all must permit</span>
            </div>
            <div className="panel-body">
              {/* Each lamp is one condition standing between a signal and a
                  spent ETH. This is an AND, not a majority: any single closed
                  interlock stops the trade. */}
              <div className="grid-2">
                {lamps.map((lamp) => (
                  <TallyRow
                    key={lamp.name}
                    state={lamp.state}
                    name={lamp.name}
                    reading={lamp.reading}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Flags</h2>
            </div>
            <div className="panel-body">
              <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
                {flags.map((flag) => (
                  <FlagChip key={flag.label} flag={flag} />
                ))}
              </div>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Totals</h2>
              <span className="label label-dim">chain {status.chain_id}</span>
            </div>
            <div className="panel-body">
              <div className="grid-4">
                <div className="stat">
                  <div className="stat-value">{status.totals.pairs}</div>
                  <div className="stat-label">Pairs seen</div>
                </div>
                <div className="stat">
                  <div className="stat-value">{status.totals.tokens}</div>
                  <div className="stat-label">Tokens</div>
                </div>
                <div className="stat">
                  <div className="stat-value">{status.totals.watchlist}</div>
                  <div className="stat-label">Watchlisted</div>
                </div>
                <div className="stat">
                  <div className="stat-value">{status.totals.open_positions}</div>
                  <div className="stat-label">Open positions</div>
                </div>
              </div>
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title">Integrations</h2>
            </div>
            <div className="panel-body">
              <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
                <span className="chip" data-tone={status.integrations.telegram_enabled ? 'neutral' : 'muted'}>
                  Telegram {status.integrations.telegram_enabled ? 'on' : 'off'}
                </span>
                <span
                  className="chip"
                  data-tone={status.integrations.telegram_approval_enabled ? 'neutral' : 'muted'}
                >
                  Approvals {status.integrations.telegram_approval_enabled ? 'on' : 'off'}
                </span>
                <span className="chip" data-tone={status.integrations.openclaw_enabled ? 'neutral' : 'muted'}>
                  Agent gateway {status.integrations.openclaw_enabled ? 'on' : 'off'}
                </span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
